import React, { useState, useRef, useEffect } from 'react';
import { Info } from '@phosphor-icons/react';
import { Switch } from './ui/switch';
import { useDutyFree } from '../context/DutyFreeContext';
import './DutyFreeCheckbox.css';

/**
 * DutyFreeCheckbox - switch для режима Duty Free цен
 *
 * Duty Free цены доступны для British Forces/Serving Personnel
 * и освобождены от уплаты VAT (настраиваемая ставка)
 *
 * Использует текстовые конфигурации из runtimeConfig:
 * - DUTY_FREE_LABEL: текст label (например, "British Forces Duty Free Prices")
 * - DUTY_FREE_TOOLTIP_TITLE: заголовок tooltip
 * - DUTY_FREE_TOOLTIP_TEXT: основной текст tooltip
 *
 * Controlled mode (#945, Phase 1): если переданы `checked` и `onChange` props,
 * компонент берёт значения из них вместо context (URL-sync в CarsPage). Без
 * props — fallback на context (uncontrolled), back-compat для CarDetailPage.
 */
const DutyFreeCheckbox = ({ className = '', showTooltip = true, checked, onChange }) => {
  const { isDutyFree, setIsDutyFree, labelText, tooltipTitle, tooltipText } = useDutyFree();
  const isControlled = checked !== undefined && onChange !== undefined;
  const switchChecked = isControlled ? checked : isDutyFree;
  const switchOnChange = isControlled ? onChange : setIsDutyFree;
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const tooltipRef = useRef(null);

  // Close tooltip when clicking outside
  useEffect(() => {
    if (!isTooltipVisible) return;

    const handleClickOutside = (e) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
        setIsTooltipVisible(false);
      }
    };

    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isTooltipVisible]);

  const handleInfoClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsTooltipVisible(!isTooltipVisible);
  };

  return (
    <div className={`duty-free-checkbox ${className}`.trim()}>
      {/* Switch */}
      <Switch
        id="duty-free-switch"
        checked={switchChecked}
        onChange={switchOnChange}
        label={labelText}
      />

      {/* Info tooltip */}
      {showTooltip && (
        <div className="duty-free-checkbox__info" ref={tooltipRef}>
          <button
            type="button"
            onClick={handleInfoClick}
            onTouchEnd={handleInfoClick}
            className="duty-free-checkbox__info-btn"
            aria-label="Show duty free information"
          >
            <Info className="duty-free-checkbox__info-icon" />
          </button>
          {/* Tooltip - показываем снизу чтобы не обрезался */}
          {/* Desktop: hover, Mobile: click toggle */}
          <div className={`duty-free-checkbox-tooltip${isTooltipVisible ? ' duty-free-checkbox-tooltip--visible' : ''}`}>
            <p className="duty-free-checkbox__tooltip-title">{tooltipTitle}</p>
            <p>{tooltipText}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DutyFreeCheckbox;
